import { X } from 'lucide-react'
import { useState, useRef } from 'react'

import { useClickOutside } from '@/hooks/use-click-outside'

export interface SelectOption {
  id: string
  name: string
}

interface MultiSelectAutocompleteProps {
  options: SelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  id?: string
  label?: string
  placeholder?: string
  error?: string
  isLoading?: boolean
  disabled?: boolean
}

function useFilteredOptions(options: SelectOption[], value: string[], searchQuery: string) {
  const selectedOptions = options.filter((opt) => value.includes(opt.id))
  const availableOptions = options.filter((opt) => !value.includes(opt.id))

  const filteredOptions = searchQuery
    ? availableOptions.filter(
        (opt) =>
          opt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opt.id.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableOptions

  return { selectedOptions, filteredOptions }
}

function SelectedTag({
  option,
  disabled,
  onRemove,
}: {
  option: SelectOption
  disabled: boolean
  onRemove: (id: string) => void
}) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-sm rounded-md border border-primary-200 dark:border-primary-800">
      <span className="font-medium">{option.name}</span>
      <span className="text-xs text-primary-600 dark:text-primary-400">({option.id})</span>
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(option.id)
          }}
          className="hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded p-0.5 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  )
}

function OptionsList({
  filteredOptions,
  searchQuery,
  onSelect,
}: {
  filteredOptions: SelectOption[]
  searchQuery: string
  onSelect: (id: string) => void
}) {
  if (filteredOptions.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        {searchQuery ? 'No matches found' : 'All options selected'}
      </div>
    )
  }

  return (
    <ul className="py-1">
      {filteredOptions.map((option) => (
        <li key={option.id}>
          <button
            type="button"
            onClick={() => {
              onSelect(option.id)
            }}
            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between group"
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">
              {option.name}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono group-hover:text-primary-600 dark:group-hover:text-primary-400">
              {option.id}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

function SelectionStatus({
  error,
  selectedCount,
  isLoading,
}: {
  error?: string
  selectedCount: number
  isLoading: boolean
}) {
  if (isLoading) {
    return <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading options...</p>
  }

  if (error) {
    return <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>
  }

  if (selectedCount > 0) {
    return (
      <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
        {selectedCount} selected
      </p>
    )
  }

  return null
}

function getInputContainerClass(error?: string, disabled?: boolean, isOpen?: boolean): string {
  const base =
    'min-h-[44px] w-full px-3 py-2 border rounded-lg shadow-sm bg-white dark:bg-gray-900 transition-all'
  const borderClass = error
    ? 'border-red-300 dark:border-red-600'
    : 'border-gray-300 dark:border-gray-600'
  const cursorClass = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'
  const focusClass = isOpen && !disabled ? 'ring-2 ring-primary-500 border-transparent' : ''
  return `${base} ${borderClass} ${cursorClass} ${focusClass}`
}

function InputContainer({
  id,
  selectedOptions,
  disabled,
  isLoading,
  isOpen,
  searchQuery,
  placeholder,
  error,
  inputRef,
  onRemove,
  onSearchChange,
  onOpen,
  onKeyDown,
}: {
  id?: string
  selectedOptions: SelectOption[]
  disabled: boolean
  isLoading: boolean
  isOpen: boolean
  searchQuery: string
  placeholder: string
  error?: string
  inputRef: React.RefObject<HTMLInputElement>
  onRemove: (id: string) => void
  onSearchChange: (value: string) => void
  onOpen: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  return (
    <div
      className={getInputContainerClass(error, disabled, isOpen)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && inputRef.current?.focus()}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          inputRef.current?.focus()
        }
      }}
    >
      <div className="flex flex-wrap gap-2 items-center">
        {selectedOptions.map((option) => (
          <SelectedTag key={option.id} option={option} disabled={disabled} onRemove={onRemove} />
        ))}

        <input
          ref={inputRef}
          id={id}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          onFocus={onOpen}
          onKeyDown={onKeyDown}
          disabled={disabled || isLoading}
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[140px] text-sm outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  )
}

function DropdownPanel({
  isOpen,
  disabled,
  isLoading,
  filteredOptions,
  searchQuery,
  onSelect,
}: {
  isOpen: boolean
  disabled: boolean
  isLoading: boolean
  filteredOptions: SelectOption[]
  searchQuery: string
  onSelect: (optionId: string) => void
}) {
  if (!isOpen || disabled || isLoading) return null

  return (
    <div className="relative mt-2">
      <div className="absolute z-10 w-full max-h-64 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <OptionsList
          filteredOptions={filteredOptions}
          searchQuery={searchQuery}
          onSelect={onSelect}
        />
      </div>
    </div>
  )
}

export function MultiSelectAutocomplete({
  options,
  value,
  onChange,
  id,
  label,
  placeholder = 'Type to search...',
  error,
  isLoading = false,
  disabled = false,
}: MultiSelectAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useClickOutside(containerRef, () => {
    setIsOpen(false)
    setSearchQuery('')
  })

  const { selectedOptions, filteredOptions } = useFilteredOptions(options, value, searchQuery)

  const handleSelect = (optionId: string) => {
    onChange([...value, optionId])
    setSearchQuery('')
    inputRef.current?.focus()
  }

  const handleRemove = (optionId: string) => {
    onChange(value.filter((vid) => vid !== optionId))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !searchQuery && selectedOptions.length > 0) {
      handleRemove(selectedOptions[selectedOptions.length - 1].id)
    }

    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchQuery('')
    }
  }

  const handleSearchChange = (val: string) => {
    setSearchQuery(val)
    if (!isOpen) setIsOpen(true)
  }

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
        >
          {label}
        </label>
      )}

      <InputContainer
        id={id}
        selectedOptions={selectedOptions}
        disabled={disabled}
        isLoading={isLoading}
        isOpen={isOpen}
        searchQuery={searchQuery}
        placeholder={placeholder}
        error={error}
        inputRef={inputRef}
        onRemove={handleRemove}
        onSearchChange={handleSearchChange}
        onOpen={() => {
          if (!disabled) setIsOpen(true)
        }}
        onKeyDown={handleKeyDown}
      />

      <DropdownPanel
        isOpen={isOpen}
        disabled={disabled}
        isLoading={isLoading}
        filteredOptions={filteredOptions}
        searchQuery={searchQuery}
        onSelect={handleSelect}
      />

      <SelectionStatus error={error} selectedCount={selectedOptions.length} isLoading={isLoading} />
    </div>
  )
}
